"use client";

import { useLocale, useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { Mail, Lock, LogIn, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useGetUser, useLogin } from "@/hooks/main-hook";
import Loading from "./Loading";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Image from "next/image";

export default function Login() {
  const navigate = useRouter();
  const locale = useLocale();
  const t = useTranslations("Auth");
  const [showPassword, setShowPassword] = useState(false);
  const { mutate, isPending } = useLogin();
  const isRtl = locale === "ar";

  const { data } = useGetUser();
  const user = data?.user;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "all" });

  const handleLogin = (data: any) => {
    mutate(data, {
      onSuccess: (res) => {
        if (res?.user?.role === "bank") {
          navigate.push(`/bank`);
          return;
        }

        navigate.push(`/dashboard`);
      },
    });
  };

  useEffect(() => {
    if (user?.role === "bank") {
      navigate.push(`/${locale}/bank`);
      return;
    }

    if (user && user?.role !== "bank") {
      navigate.push(`/${locale}/dashboard`);
    }
  }, [user, navigate, locale]);

  return (
    <main
      dir={isRtl ? "rtl" : "ltr"}
      className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[#050505] text-[#EDE5D6]  overflow-hidden font-cairo"
    >
      <section className="hidden lg:flex lg:col-span-6 relative overflow-hidden group">
        <motion.div
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 z-0"
        >
          <Image
            src="/auth.png"
            alt="Saudi Skyline Vision 2030"
            fill
            className="opacity-100 transition-all duration-1000"
          />
        </motion.div>

        <div
          className={`absolute inset-0 bg-gradient-to-${isRtl ? "left" : "right"} from-white via-white/40 to-transparent z-10`}
        />

        <div className="absolute top-10 left-10 z-20">
          <div className="text-3xl font-black text-white/50 tracking-tighter uppercase font-sans">
            SNA<span className="text-[#C8A96E]">.</span>
          </div>
        </div>

        <div
          className={`absolute bottom-8 ${isRtl ? "left-8" : "right-8"} z-20 flex items-center gap-6 text-[10px] text-white/40 font-bold uppercase tracking-[0.4em]`}
        >
          <span>Real Estate</span>
          <div className="w-1 h-1 bg-white/20 rounded-full" />
          <span>Banking</span>
          <div className="w-1 h-1 bg-white/20 rounded-full" />
          <span>Verified Protocol</span>
        </div>
      </section>

      <section className="lg:col-span-6 flex flex-col items-center justify-center p-8 md:p-20 bg-[#050505] text-[#EDE5D6]   relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-[#C8A96E]/5 blur-[120px] rounded-full pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, x: isRtl ? -30 : 30 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="mb-16">
            <h1 className="text-5xl md:text-7xl  mb-4 tracking-tighter leading-tight">
              {t("welcome")}
              <span className="text-[#C8A96E]">.</span>
            </h1>
            <p className="text-gray-500 text-xl font-light leading-relaxed">
              {t("subtitle")}
              <span className="text-white font-medium italic block mt-1">
                {isRtl
                  ? "شريكك في التطوير، التسويق، والتمويل البنكي الآمن."
                  : "Your partner in development, marketing, and secure finance."}
              </span>
            </p>
          </div>

          <form onSubmit={handleSubmit(handleLogin)} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-[#C8A96E] uppercase tracking-[0.3em] px-1 font-cairo">
                {t("email")}
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 start-0 flex items-center ps-5 pointer-events-none text-gray-400 group-focus-within:text-[#C8A96E] transition-colors">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  className={`w-full  border ${errors.email ? "border-red-500/50" : "border-[#F5F5F5] group-hover:border-gray-200"} rounded-xl py-4.5 ps-14 pe-4 text-white focus:outline-none focus:border-[#C8A96E]/50 focus:ring-4 focus:ring-[#C8A96E]/5 transition-all duration-300 placeholder:text-gray-600 bg-transparent`}
                  placeholder="investor@company.com"
                  {...register("email", {
                    required: t("required"),
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: t("invalid_email"),
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1.5 px-1">
                  {errors.email.message as string}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-[#C8A96E] uppercase tracking-[0.3em] px-1 font-cairo">
                {t("password")}
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 start-0 flex items-center ps-5 pointer-events-none text-gray-400 group-focus-within:text-[#C8A96E] transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full bg-transparent border ${errors.password ? "border-red-500/50" : "border-[#F5F5F5] group-hover:border-gray-200"} rounded-xl py-4.5 ps-14 pe-14 text-white focus:outline-none focus:border-[#C8A96E]/50 focus:ring-4 focus:ring-[#C8A96E]/5 transition-all duration-300 placeholder:text-gray-600`}
                  placeholder="••••••••"
                  {...register("password", {
                    required: t("required"),
                    minLength: { value: 6, message: t("min_password") },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 end-0 pe-5 flex items-center text-gray-400 hover:text-gray-900 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 mt-1.5 px-1">
                  {errors.password.message as string}
                </p>
              )}
            </div>

            {/* Forgot Password */}
            <div className={`flex ${isRtl ? "justify-start" : "justify-end"}`}>
              <Link
                href="/auth/reset-password"
                className="text-sm font-medium text-gray-600 hover:text-[#C8A96E] border-b border-transparent hover:border-[#C8A96E] pb-0.5 transition-all duration-300"
              >
                {t("forgot")}
              </Link>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              {isPending ? (
                <div className="flex justify-center p-4 rounded-xl">
                  <Loading />
                </div>
              ) : (
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.01, translateY: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#C8A96E] hover:bg-[#D9B882] text-black font-black py-4.5 rounded-xl shadow-[0_15px_30px_rgba(200,169,110,0.2)] transition-all duration-300 flex items-center justify-center gap-3 uppercase tracking-tighter text-lg"
                >
                  {t("login")}
                  <LogIn size={20} />
                </motion.button>
              )}
            </div>
          </form>

          {/* Registration Link */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 font-light text-lg">
              {t("register")}{" "}
              <Link
                href="/auth/register"
                className="font-bold text-white hover:text-[#C8A96E] border-b-2 border-gray-950/10 hover:border-[#C8A96E] transition-all pb-0.5 ml-1"
              >
                {t("signup")}
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Footer Info */}
        <div className="absolute bottom-10 text-[11px] text-gray-800 tracking-[0.3em] font-black uppercase z-20">
          Official SNA Network © 2026 Legal Protocol
        </div>
      </section>
    </main>
  );
}
